import {
  DEFAULT_NODES_IDS,
  DROPZONE_TYPES,
  DROPZONES_NAMES,
  WORKFLOW_INITIAL_OBJECT,
} from 'components/DesignOrchestrationDiagram/DesignOrchestrationDiagram.constans';
import {
  IDropZoneStepGroup,
  IDropZoneWorkflow,
  IDropZoneWorkflowStep,
} from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import { IDropZoneWorkflowState } from 'interfaces/designOrchestration/designOrchestrationState.interface';

// Utility function to find a specific step in the workflow by workflowStepId
export const findStepInWorkflow = (
  stepGroup: IDropZoneStepGroup | null,
  targetWorkflowStepId: string
): IDropZoneWorkflowStep | null => {
  if (!stepGroup?.steps) {
    return null;
  }

  for (const step of stepGroup.steps) {
    if (String(step.workflowStepId) === targetWorkflowStepId) {
      return step;
    }

    // Search in nested groups
    if (step.nestedStepGroup) {
      const found = findStepInWorkflow(
        step.nestedStepGroup,
        targetWorkflowStepId
      );
      if (found) {
        return found;
      }
    }

    if (step.failbackStepGroup) {
      const found = findStepInWorkflow(
        step.failbackStepGroup,
        targetWorkflowStepId
      );
      if (found) {
        return found;
      }
    }
  }

  // Search in next step group
  if (stepGroup.nextStepGroup) {
    return findStepInWorkflow(stepGroup.nextStepGroup, targetWorkflowStepId);
  }

  return null;
};

// Utility function to recursively find all related step IDs including drop zones
export const findAllRelatedStepIds = (
  stepId: string,
  workflowState: IDropZoneWorkflowState
): string[][] => {
  const relatedIds: string[] = [stepId];
  const dropZoneIds: string[] = [];
  const relatedUniqueIds: string[] = [];

  const findStepInWorkflowRecursive = (
    workflow: IDropZoneWorkflow,
    targetId: string
  ): IDropZoneWorkflowStep | null => {
    if (!workflow) {
      return null;
    }

    const searchInStepGroup = (
      stepGroup: IDropZoneStepGroup
    ): IDropZoneWorkflowStep | null => {
      if (!stepGroup?.steps) {
        return null;
      }

      for (const step of stepGroup.steps) {
        if (
          String(step.workflowStepId) === targetId ||
          String(step.uniqueId) === targetId
        ) {
          return step;
        }

        // Search in nested step groups
        if (step.nestedStepGroup) {
          const found = searchInStepGroup(step.nestedStepGroup);
          if (found) {
            return found;
          }
        }

        // Search in failback step groups
        if (step.failbackStepGroup) {
          const found = searchInStepGroup(step.failbackStepGroup);
          if (found) {
            return found;
          }
        }
      }

      // Search in next step group
      if (stepGroup.nextStepGroup) {
        return searchInStepGroup(stepGroup.nextStepGroup);
      }

      return null;
    };

    // Search in main workflow
    if (workflow.nextStepGroup) {
      const found = searchInStepGroup(workflow.nextStepGroup);
      if (found) {
        return found;
      }
    }

    // Search in failback workflow
    if (workflow.failbackStepGroup) {
      const found = searchInStepGroup(workflow.failbackStepGroup);
      if (found) {
        return found;
      }
    }

    return null;
  };

  const collectStepIds = (stepGroup: IDropZoneStepGroup): void => {
    if (!stepGroup?.steps) {
      return;
    }

    stepGroup.steps.forEach((step) => {
      const stepIdToAdd = String(step.workflowStepId || step.uniqueId);
      const uniqueIdToAdd = String(step.uniqueId);
      if (!relatedIds.includes(stepIdToAdd)) {
        relatedIds.push(stepIdToAdd);
      }

      if (!relatedUniqueIds.includes(uniqueIdToAdd)) {
        relatedUniqueIds.push(uniqueIdToAdd);
      }

      // Recursively collect from nested groups
      if (step.nestedStepGroup) {
        collectStepIds(step.nestedStepGroup);
      }

      // Recursively collect from failback groups
      if (step.failbackStepGroup) {
        collectStepIds(step.failbackStepGroup);
      }
    });

    // Collect from next step group
    if (stepGroup.nextStepGroup) {
      collectStepIds(stepGroup.nextStepGroup);
    }
  };

  const findStepGroupContaining = (
    workflow: IDropZoneWorkflow,
    targetId: string
  ): IDropZoneStepGroup | null => {
    if (!workflow) {
      return null;
    }

    const searchInStepGroup = (
      stepGroup: IDropZoneStepGroup
    ): IDropZoneStepGroup | null => {
      if (!stepGroup?.steps) {
        return null;
      }

      // Check if this stepGroup contains our target step
      const hasTargetStep = stepGroup.steps.some(
        (step) =>
          String(step.workflowStepId) === targetId ||
          String(step.uniqueId) === targetId
      );

      if (hasTargetStep) {
        return stepGroup;
      }

      // Search in nested step groups
      for (const step of stepGroup.steps) {
        if (step.nestedStepGroup) {
          const found = searchInStepGroup(step.nestedStepGroup);
          if (found) {
            return found;
          }
        }

        // Search in failback step groups
        if (step.failbackStepGroup) {
          const found = searchInStepGroup(step.failbackStepGroup);
          if (found) {
            return found;
          }
        }
      }

      // Search in next step group
      if (stepGroup.nextStepGroup) {
        return searchInStepGroup(stepGroup.nextStepGroup);
      }

      return null;
    };

    // Search in main workflow
    if (workflow.nextStepGroup) {
      const found = searchInStepGroup(workflow.nextStepGroup);
      if (found) {
        return found;
      }
    }

    // Search in failback workflow
    if (workflow.failbackStepGroup) {
      const found = searchInStepGroup(workflow.failbackStepGroup);
      if (found) {
        return found;
      }
    }

    return null;
  };

  try {
    // Find the current step in the workflow
    const currentStep = findStepInWorkflowRecursive(
      workflowState.workflow,
      stepId
    );

    if (currentStep) {
      // Collect IDs from nested step groups
      if (currentStep.nestedStepGroup) {
        collectStepIds(currentStep.nestedStepGroup);
      }

      // Collect IDs from failback step groups
      if (currentStep.failbackStepGroup) {
        collectStepIds(currentStep.failbackStepGroup);
      }

      // New logic: If stepGroup has exactly 2 steps (current + 1 parallel),
      // include the parallel step's nestedStepGroup
      const containingStepGroup = findStepGroupContaining(
        workflowState.workflow,
        stepId
      );

      if (
        containingStepGroup &&
        containingStepGroup.steps.length === 3 &&
        containingStepGroup.steps[2].stepTitle === DROPZONES_NAMES.PARALLEL
      ) {
        // Find the parallel step (the other step in the same group)
        const parallelStep = containingStepGroup.steps.find(
          (step) =>
            String(step.workflowStepId) !== stepId &&
            String(step.uniqueId) !== stepId
        );

        // If parallel step has nestedStepGroup, collect its IDs
        if (parallelStep?.nestedStepGroup) {
          collectStepIds(parallelStep.nestedStepGroup);
        }
      }
    }
  } catch (error) {
    console.error('Error finding related step IDs:', error);
  }

  // Combine module IDs and drop zone IDs
  return [[...relatedIds, ...dropZoneIds], [...relatedUniqueIds]];
};

// Utility function to filter step modules based on target step and related IDs
export const filterStepModulesForDeletion = (
  stepModules: any[],
  targetStep: IDropZoneWorkflowStep | null,
  relatedStepIds: string[],
  relatedUniqueIds: string[]
) => {
  return stepModules.filter((stepModule) => {
    const moduleUniqueId = stepModule.uniqueId;
    const moduleStepIndex = String(stepModule.stepIndex);

    // Check against the target step's identifiers
    const shouldRemoveByTarget =
      targetStep &&
      (moduleUniqueId === targetStep.uniqueId ||
        moduleStepIndex === String(targetStep.stepIndex));

    // Check if this module should be removed by any of the related IDs
    const shouldRemoveByRelated = relatedStepIds.some(
      (relatedId) =>
        relatedId === moduleUniqueId || relatedId === moduleStepIndex
    );

    const shouldRemoveByUniqueId = relatedUniqueIds.some(
      (relatedUniqueId) => relatedUniqueId === moduleUniqueId
    );

    const shouldRemove =
      shouldRemoveByTarget || shouldRemoveByRelated || shouldRemoveByUniqueId;

    return !shouldRemove;
  });
};

// Helper function for removing steps from a step group
const removeStepsFromGroup = (
  group: IDropZoneStepGroup | null,
  stepIdsToRemove: string[]
): IDropZoneStepGroup | null => {
  if (!group) {
    return null;
  }

  // Create a set of additional step IDs to remove (parallel steps following deleted steps)
  const additionalStepIdsToRemove: string[] = [];

  // Check each step and mark following parallel steps for removal
  group.steps.forEach((step, index) => {
    const stepId = String(step.workflowStepId || step.uniqueId);

    // If this step is being removed, check if the next step is PARALLEL
    if (stepIdsToRemove.includes(stepId) && group.steps.length === 2) {
      const nextStep = group.steps[index + 1];
      if (
        nextStep &&
        (nextStep.stepTitle === DROPZONES_NAMES.PARALLEL ||
          nextStep.stepTitle === DROPZONES_NAMES.PARALLEL_FAILBACK ||
          nextStep.stepTitle === DROPZONES_NAMES.PARALLEL_GLOBAL_FAILBACK)
      ) {
        const nextStepId = String(nextStep.workflowStepId || nextStep.uniqueId);
        if (
          !stepIdsToRemove.includes(nextStepId) &&
          !additionalStepIdsToRemove.includes(nextStepId)
        ) {
          additionalStepIdsToRemove.push(nextStepId);
        }
      }
    }
  });

  // Combine original and additional step IDs to remove
  const allStepIdsToRemove = [...stepIdsToRemove, ...additionalStepIdsToRemove];

  // Remove steps that match stepIdsToRemove (including additional parallel steps)
  const filteredSteps = group.steps.filter(
    (step) =>
      !allStepIdsToRemove.includes(String(step.workflowStepId)) &&
      !allStepIdsToRemove.includes(String(step.uniqueId))
  );

  // Recursively clean nested and failback groups for remaining steps
  const cleanedSteps = filteredSteps.map((step) => ({
    ...step,
    nestedStepGroup: removeStepsFromGroup(
      step.nestedStepGroup,
      allStepIdsToRemove
    ),
    failbackStepGroup: removeStepsFromGroup(
      step.failbackStepGroup,
      allStepIdsToRemove
    ),
  }));

  // Check for additional steps to remove in nextStepGroup (SUCCEDING/PREDECESSOR)
  const additionalNextStepIdsToRemove: string[] = [];
  if (
    group.steps.some(
      (step) =>
        stepIdsToRemove.includes(String(step.workflowStepId)) ||
        stepIdsToRemove.includes(String(step.uniqueId))
    ) &&
    cleanedSteps.length === 0 &&
    group.nextStepGroup &&
    allStepIdsToRemove.length > 0
  ) {
    group.nextStepGroup.steps.forEach((nextStep) => {
      if (
        nextStep.stepTitle === DROPZONES_NAMES.SUCCEDING ||
        nextStep.stepTitle === DROPZONES_NAMES.SUCCEDING_FAILBACK ||
        nextStep.stepTitle === DROPZONES_NAMES.PREDECESSOR_FAILBACK ||
        nextStep.stepTitle === DROPZONES_NAMES.PREDECESSOR // Only if original steps were removed
      ) {
        const nextStepId = String(nextStep.workflowStepId || nextStep.uniqueId);
        if (!allStepIdsToRemove.includes(nextStepId)) {
          additionalNextStepIdsToRemove.push(nextStepId);
        }
      }
    });
  }

  // Update allStepIdsToRemove with additional next step IDs
  const finalStepIdsToRemove = [
    ...allStepIdsToRemove,
    ...additionalNextStepIdsToRemove,
  ];

  // If no steps remain and there's a nextStepGroup, return the nextStepGroup
  if (cleanedSteps.length === 0 && group.nextStepGroup) {
    return removeStepsFromGroup(group.nextStepGroup, finalStepIdsToRemove);
  }

  // Clean the nextStepGroup with the updated removal list
  const cleanedNextStepGroup = removeStepsFromGroup(
    group.nextStepGroup,
    finalStepIdsToRemove
  );

  // Return the cleaned group
  return {
    ...group,
    steps: cleanedSteps,
    nextStepGroup: cleanedNextStepGroup,
  };
};

export function removeModulesFromWorkflow(
  workflowState: IDropZoneWorkflowState,
  stepIdsToRemove: string[]
): IDropZoneWorkflowState {
  try {
    const workflow = JSON.parse(
      JSON.stringify(workflowState)
    ) as IDropZoneWorkflowState;

    // Find parent steps in both main and failback workflows
    const parentStepsToRemove = [
      ...findParentStepsToRemove(
        workflow.workflow.nextStepGroup,
        stepIdsToRemove,
        null,
        null
      ),
      ...findParentStepsToRemove(
        workflow.workflow.failbackStepGroup,
        stepIdsToRemove,
        null,
        null
      ),
    ];

    // Function to update associatedMainNodeId for remaining steps
    const updateAssociatedMainNodeIds = (
      stepGroup: IDropZoneStepGroup | null,
      parentGroup?: IDropZoneStepGroup
    ): IDropZoneStepGroup | null => {
      if (!stepGroup) {
        return null;
      }

      // First, recursively process nextStepGroup to get the main node from it
      let updatedNextStepGroup = stepGroup.nextStepGroup;
      let nextStepGroupMainNodeId: string | null = null;

      if (updatedNextStepGroup) {
        // Recursively update the nextStepGroup first
        updatedNextStepGroup = updateAssociatedMainNodeIds(
          updatedNextStepGroup,
          stepGroup
        );

        // Find the first non-dropzone step in nextStepGroup to use as main node
        const nextStepGroupMainStep = updatedNextStepGroup?.steps.find(
          (step) => {
            const isDropZone = Object.values(DROPZONES_NAMES).includes(
              step.stepTitle as any
            );
            return !isDropZone;
          }
        );

        nextStepGroupMainNodeId = nextStepGroupMainStep
          ? String(
              nextStepGroupMainStep.workflowStepId ||
                nextStepGroupMainStep.uniqueId
            )
          : null;
      }

      // Update current group steps with the main node ID from nextStepGroup
      const updatedSteps = stepGroup.steps.map((step) => {
        const isDropZone = Object.values(DROPZONES_NAMES).includes(
          step.stepTitle as any
        );
        const isLastPredecessor =
          stepGroup.nextStepGroup?.steps &&
          !stepGroup.nextStepGroup.steps.length;

        const updatedStep: IDropZoneWorkflowStep = {
          ...step,
          nestedStepGroup: updateAssociatedMainNodeIds(
            step.nestedStepGroup,
            stepGroup
          ),
          failbackStepGroup: updateAssociatedMainNodeIds(
            step.failbackStepGroup,
            stepGroup
          ),
          nestGroupId: step.nestGroupId?.filter(
            (groupId) => !stepIdsToRemove.includes(String(groupId))
          ),
        };
        // Only update associatedMainNodeId for dropzone steps, and only if we found a main node
        if (isDropZone && nextStepGroupMainNodeId) {
          updatedStep.associatedMainNodeId = nextStepGroupMainNodeId;
          updatedStep.nestGroupId = [
            ...(updatedStep.nestGroupId || []),
            nextStepGroupMainNodeId,
          ];
        }

        updateFailbackStep(
          parentGroup,
          step,
          updatedStep,
          isLastPredecessor,
          allStepsToRemove
        );
        updateSucceedingAndParallelStep(parentGroup, step, updatedStep);

        return updatedStep;
      });

      return {
        ...stepGroup,
        steps: updatedSteps,
        nextStepGroup: updatedNextStepGroup,
      };
    };

    // Combine original steps with parent steps to remove
    const allStepsToRemove = [...stepIdsToRemove, ...parentStepsToRemove];

    // Clean main workflow
    let cleanedNextStepGroup = removeStepsFromGroup(
      workflow.workflow.nextStepGroup,
      allStepsToRemove
    );

    // Clean failback workflow
    let cleanedFailbackStepGroup = removeStepsFromGroup(
      workflow.workflow.failbackStepGroup,
      allStepsToRemove
    );

    // Update associatedMainNodeId for remaining steps
    cleanedNextStepGroup = updateAssociatedMainNodeIds(cleanedNextStepGroup);
    cleanedFailbackStepGroup = updateAssociatedMainNodeIds(
      cleanedFailbackStepGroup
    );

    // Check if both stepGroups only contain dropzones (meaning no real modules left)
    const nextStepGroupOnlyDropzones = hasOnlyDropzones(cleanedNextStepGroup);
    const failbackStepGroupOnlyDropzones = hasOnlyDropzones(
      cleanedFailbackStepGroup
    );

    // If both stepGroups only have dropzones or are empty, return to initial state
    if (nextStepGroupOnlyDropzones && failbackStepGroupOnlyDropzones) {
      return {
        ...workflow,
        workflow: WORKFLOW_INITIAL_OBJECT.workflow,
      };
    }

    // Ensure nextStepGroup is never null and has at least the initial dropzone if empty
    let finalNextStepGroup = cleanedNextStepGroup;

    // If nextStepGroup is null or has no steps (empty), provide the default initial dropzone
    if (!finalNextStepGroup?.steps || finalNextStepGroup.steps.length === 0) {
      finalNextStepGroup = {
        workflowStepGroupId: -1,
        parentWorkflowStepGroupId: null,
        steps: [
          {
            workflowStepId: DEFAULT_NODES_IDS.INITIAL_DROPZONE,
            workflowStepGroupId: null,
            stepTitle: DROPZONES_NAMES.SUCCEDING,
            stepId: DROPZONE_TYPES.SUCCEDING,
            stepCondition: null,
            stepIndex: null,
            module: {
              id: null,
              textId: '',
              version: '',
              name: '',
              description: '',
              parameters: [],
            },
            paramGroups: [],
            nestedStepGroup: null,
            failbackStepGroup: null,
          },
        ],
        nextStepGroup: null,
      };
      // If nextStepGroup only has dropzones and failback has modules, ensure we have the initial dropzone
    } else if (nextStepGroupOnlyDropzones && !failbackStepGroupOnlyDropzones) {
      // Check if it already has the initial dropzone
      const hasInitialDropzone = finalNextStepGroup.steps.some(
        (step) => step.workflowStepId === DEFAULT_NODES_IDS.INITIAL_DROPZONE
      );

      if (!hasInitialDropzone) {
        // Replace with the initial dropzone structure
        finalNextStepGroup = {
          workflowStepGroupId: -1,
          parentWorkflowStepGroupId: null,
          steps: [
            {
              workflowStepId: DEFAULT_NODES_IDS.INITIAL_DROPZONE,
              workflowStepGroupId: null,
              stepTitle: DROPZONES_NAMES.SUCCEDING,
              stepId: DROPZONE_TYPES.SUCCEDING,
              stepCondition: null,
              stepIndex: null,
              module: {
                id: null,
                textId: '',
                version: '',
                name: '',
                description: '',
                parameters: [],
              },
              paramGroups: [],
              nestedStepGroup: null,
              failbackStepGroup: null,
            },
          ],
          nextStepGroup: null,
        };
      }
    }

    /**
     * Recursively cleans empty nestedStepGroup properties by setting them to null
     * when their steps array is empty
     */
    cleanEmptyNestedStepGroups(finalNextStepGroup);

    return {
      ...workflow,
      workflow: {
        ...workflow.workflow,
        nextStepGroup: finalNextStepGroup,
        failbackStepGroup: cleanedFailbackStepGroup,
      },
    };
  } catch (error) {
    console.error('removeModulesFromWorkflow: Error removing modules:', error);
    return workflowState;
  }
}

// Find parent steps that should also be removed
const findParentStepsToRemove = (
  stepGroup: IDropZoneStepGroup | null,
  targetStepIds: string[],
  parentGroup?: IDropZoneStepGroup | null,
  grandParentGroup?: IDropZoneStepGroup | null
): string[] => {
  const parentStepsToRemove: string[] = [];

  if (!stepGroup) {
    return parentStepsToRemove;
  }

  // Check if this group contains any of our target steps to eliminate
  const targetStepsInThisGroup = stepGroup.steps.filter((step) =>
    targetStepIds.includes(String(step.workflowStepId || step.uniqueId))
  );

  // If we found target steps in this group and we have a parent, check if parent should be removed
  if (targetStepsInThisGroup.length > 0 && parentGroup) {
    // Check if this group (where the target step is) has SUCCEDING or PREDECESSOR steps
    const isSucceedingOrPredecessor = parentGroup.steps.some(
      (step) =>
        step.stepTitle === DROPZONES_NAMES.SUCCEDING ||
        step.stepTitle === DROPZONES_NAMES.PREDECESSOR ||
        step.stepTitle === DROPZONES_NAMES.SUCCEDING_FAILBACK ||
        step.stepTitle === DROPZONES_NAMES.PREDECESSOR_FAILBACK
    );

    if (
      isSucceedingOrPredecessor &&
      grandParentGroup &&
      !grandParentGroup?.steps?.length
    ) {
      parentStepsToRemove.push(String(parentGroup.steps[0].workflowStepId));
    }
  }

  // Recursively check nested and failback groups (without passing parentStepId)
  stepGroup.steps.forEach((step) => {
    if (step.nestedStepGroup) {
      parentStepsToRemove.push(
        ...findParentStepsToRemove(step.nestedStepGroup, targetStepIds)
      );
    }

    if (step.failbackStepGroup) {
      parentStepsToRemove.push(
        ...findParentStepsToRemove(step.failbackStepGroup, targetStepIds)
      );
    }
  });

  // Check nextStepGroup - ONLY here we pass the currentStepId as parentStepId
  if (stepGroup.nextStepGroup) {
    // For nextStepGroup, we need to pass a parentStepId based on steps in current group
    parentStepsToRemove.push(
      ...findParentStepsToRemove(
        stepGroup.nextStepGroup,
        targetStepIds,
        stepGroup,
        parentGroup
      )
    );
  }

  return parentStepsToRemove;
};

// Helper function to check if a stepGroup only contains dropzones
const hasOnlyDropzones = (stepGroup: IDropZoneStepGroup | null): boolean => {
  if (!stepGroup?.steps.length) {
    return true;
  }

  // Check if all steps are dropzones (have stepTitle that matches DROPZONES_NAMES)
  const allAreDropzones = stepGroup.steps.every((step) =>
    Object.values(DROPZONES_NAMES).includes(step.stepTitle as any)
  );

  // If current level only has dropzones, check nested levels
  if (allAreDropzones) {
    // If there's a nextStepGroup, check if it also only has dropzones
    if (stepGroup.nextStepGroup) {
      return hasOnlyDropzones(stepGroup.nextStepGroup);
    }
    return true;
  }

  return false;
};

export function updateFailbackStep(
  parentGroup: IDropZoneStepGroup | undefined,
  step: IDropZoneWorkflowStep,
  updatedStep: IDropZoneWorkflowStep,
  isLastPredecessor: boolean | undefined = false,
  allStepsToRemove: string[] = []
) {
  const isGroupedStepNode = parentGroup?.steps.some((parentStep) => {
    const nodeGroupIdSet = new Set(step.nestGroupId || []);
    return parentStep.nestGroupId
      ?.filter((groupId) => !allStepsToRemove.includes(groupId))
      .some((groupId) => nodeGroupIdSet.has(groupId));
  });

  const isGroupedNextStepNode = parentGroup?.nextStepGroup?.steps.some(
    (parentStep) => {
      const nodeGroupIdSet = new Set(step.nestGroupId || []);
      return parentStep.nestGroupId
        ?.filter((groupId) => !allStepsToRemove.includes(groupId))
        .some((groupId) => nodeGroupIdSet.has(groupId));
    }
  );

  const isFailbackStep =
    updatedStep.stepType === 'failback' ||
    updatedStep.stepId === DROPZONE_TYPES.PREDECESSOR_FAILBACK;
  const insideNested =
    step.nestGroupId?.filter((groupId) => !allStepsToRemove.includes(groupId))
      .length || false;
  const isGlobalFailback = updatedStep.stepType === 'globalFailback';

  // Update also last predecessor associeateMainNodeId for his parend id and change it for succeeding
  if (isLastPredecessor) {
    updatedStep.stepId = DROPZONE_TYPES.SUCCEDING;
    updatedStep.stepTitle = DROPZONES_NAMES.SUCCEDING;
    updatedStep.associatedMainNodeId = parentGroup?.steps[0]?.workflowStepId;
  }

  if (
    !isGroupedStepNode &&
    !isGroupedNextStepNode &&
    (isFailbackStep || insideNested)
  ) {
    updatedStep.workflowStepGroupId = parseInt(
      String(updatedStep.associatedMainNodeId || updatedStep.workflowStepId)
    );
  }

  if (!isGroupedStepNode && isGlobalFailback) {
    updatedStep.workflowStepGroupId = null;
    updatedStep.stepTitle = DROPZONES_NAMES.GLOBAL_FAILBACK_STEPS;
    updatedStep.stepId = DROPZONE_TYPES.GLOBAL_FAILBACK_STEPS;
    updatedStep.workflowStepId = DEFAULT_NODES_IDS.INITIAL_FAILBACK_DROPZONE;
  }

  // Last nested failback
  if (
    isLastPredecessor &&
    step.stepId === DROPZONE_TYPES.PREDECESSOR_FAILBACK &&
    step.stepType !== 'globalFailback'
  ) {
    updatedStep.stepId = !isGroupedNextStepNode
      ? DROPZONE_TYPES.NESTED_FAILBACK
      : DROPZONE_TYPES.SUCCEDING_FAILBACK;
    updatedStep.stepTitle = !isGroupedNextStepNode
      ? DROPZONES_NAMES.FAILBACK
      : DROPZONES_NAMES.SUCCEDING;
    updatedStep.stepType = !isGroupedNextStepNode
      ? 'nestedFailback'
      : undefined;
  }

  // Last nested module
  if (
    isLastPredecessor &&
    !isGroupedStepNode &&
    step.stepId === DROPZONE_TYPES.PREDECESSOR &&
    step.workflowStepGroupId
  ) {
    updatedStep.stepId = DROPZONE_TYPES.NESTED;
    updatedStep.stepTitle = DROPZONES_NAMES.NESTED;
    updatedStep.stepType = 'nested';
  }
}

export function updateSucceedingAndParallelStep(
  parentGroup: IDropZoneStepGroup | undefined,
  step: IDropZoneWorkflowStep,
  updatedStep: IDropZoneWorkflowStep
) {
  const parentWorflowStepId = String(parentGroup?.steps[0].workflowStepId);
  const parendNextWorflowStep = String(
    parentGroup?.nextStepGroup?.steps[0].workflowStepId
  );
  const isSucceeding = [
    DROPZONE_TYPES.SUCCEDING,
    DROPZONE_TYPES.SUCCEDING_FAILBACK,
    DROPZONE_TYPES.SUCCEDING_GLOBAL_FAILBACK,
  ].includes(String(step.stepId) as DROPZONE_TYPES);

  const isParallel = [
    DROPZONE_TYPES.PARALLEL,
    DROPZONE_TYPES.PARALLEL_FAILBACK,
    DROPZONE_TYPES.PARALLEL_GLOBAL_FAILBACK,
  ].includes(String(step.stepId) as DROPZONE_TYPES);

  // Update asssociatedMainNodeId to match aligment for succeding nodes
  if (isSucceeding) {
    updatedStep.associatedMainNodeId = parentWorflowStepId;
    updatedStep.nestGroupId = [
      ...(updatedStep?.nestGroupId || []),
      parentWorflowStepId,
    ];
  }
  if (isParallel) {
    updatedStep.associatedMainNodeId = parendNextWorflowStep;
    updatedStep.nestGroupId = [
      ...(updatedStep?.nestGroupId || []),
      parendNextWorflowStep,
    ];
  }
}

export function cleanEmptyNestedStepGroups(
  stepGroup: IDropZoneStepGroup | null | undefined
) {
  if (!stepGroup) {
    return null;
  }

  // Process steps array if it exists
  if (stepGroup.steps && Array.isArray(stepGroup.steps)) {
    stepGroup.steps = stepGroup.steps.map((step) => {
      // Clean nestedStepGroup recursively
      if (step.nestedStepGroup) {
        if (
          step.nestedStepGroup.steps &&
          step.nestedStepGroup.steps.length === 0
        ) {
          step.nestedStepGroup = null;
        } else {
          step.nestedStepGroup = cleanEmptyNestedStepGroups(
            step.nestedStepGroup
          );
        }
      }

      // Clean failbackStepGroup recursively
      if (step.failbackStepGroup) {
        if (
          step.failbackStepGroup.steps &&
          step.failbackStepGroup.steps.length === 0
        ) {
          step.failbackStepGroup = null;
        } else {
          step.failbackStepGroup = cleanEmptyNestedStepGroups(
            step.failbackStepGroup
          );
        }
      }

      return step;
    });
  }

  // Process nextStepGroup recursively
  if (stepGroup.nextStepGroup) {
    if (
      stepGroup.nextStepGroup.steps &&
      stepGroup.nextStepGroup.steps.length === 0
    ) {
      stepGroup.nextStepGroup = null;
    } else {
      stepGroup.nextStepGroup = cleanEmptyNestedStepGroups(
        stepGroup.nextStepGroup
      );
    }
  }

  return stepGroup;
}
